import type { AreaCode, PropertyAreaEntry } from './properties.schemas';

/** The six that reconcile against USABLE_AREA. LETTABLE_AREA is deliberately absent. */
export const COMMERCIAL_USE_AREA_CODES = [
  'OFFICE_AREA',
  'RETAIL_AREA',
  'SHOP_AREA',
  'STORAGE_AREA',
  'HALL_AREA',
  'GASTRONOMY_AREA',
] as const satisfies ReadonlyArray<AreaCode>;

/** What the editor offers on a Gewerbe object, and only there. */
export const COMMERCIAL_AREA_CODES = [
  ...COMMERCIAL_USE_AREA_CODES,
  'LETTABLE_AREA',
  'PARKING_SPACES',
] as const satisfies ReadonlyArray<AreaCode>;

/** Never offered on a Gewerbe object. Declared beside the field set (BL-069). */
export const RESIDENTIAL_AREA_CODES = [
  'LIVING_AREA',
  'BALCONY_AREA',
  'BEDROOMS',
  'BATHROOMS',
] as const satisfies ReadonlyArray<AreaCode>;

/** Declared beside the field set, not per renderer: these carry counts, not areas. */
export const AREA_COUNT_CODES = [
  'ROOMS',
  'BEDROOMS',
  'BATHROOMS',
  'FLOORS_TOTAL',
  'FLOOR',
  'PARKING_SPACES',
] as const satisfies ReadonlyArray<AreaCode>;

export type AreaSplit = {
  readonly sum: number;
  readonly usableArea: number;
  readonly remainder: number;
  readonly exceeded: boolean;
};

const USE_AREA_CODE_SET = new Set<AreaCode>(COMMERCIAL_USE_AREA_CODES);

function roundToCents(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Sum of the six use parts, rounded to two decimals before comparison
 * (BL-008) so float addition never trips the guard on an exact reconcile.
 */
export function useSplitSumOf(entries: ReadonlyArray<PropertyAreaEntry>): number {
  const sum = entries
    .filter((entry) => USE_AREA_CODE_SET.has(entry.type))
    .reduce((total, entry) => total + entry.value, 0);

  return roundToCents(sum);
}

/**
 * `null` when there is no whole (`USABLE_AREA`), or no use part — nothing to
 * explain (Decision 6).
 */
export function areaSplitOf(entries: ReadonlyArray<PropertyAreaEntry>): AreaSplit | null {
  const usableArea = entries.find((entry) => entry.type === 'USABLE_AREA')?.value;

  if (usableArea === undefined) return null;

  const hasUsePart = entries.some((entry) => USE_AREA_CODE_SET.has(entry.type));

  if (!hasUsePart) return null;

  const sum = useSplitSumOf(entries);
  const remainder = roundToCents(usableArea - sum);

  return { sum, usableArea, remainder, exceeded: remainder < 0 };
}
